import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CloudOff, RefreshCw, Trash2, Upload } from "lucide-react";
import { useState } from "react";

import { Button, Card, EmptyState, Pill } from "@/components/ui";
import { storageStatus } from "@/repositories/storageRepository";
import { cn } from "@/lib/cn";
import { formatRelativeTime, plural } from "@/lib/format";
import {
  outboxRepository,
  type OutboxOperation,
  type SyncState,
} from "@/repositories/outboxRepository";
import { useSyncStore } from "@/stores/syncStore";
import { syncNow } from "@/sync/engine";

/**
 * The sync centre.
 *
 * Not in the supplied designs, and required by the architecture: a cashier must
 * be able to see what has not reached the server, and an owner must be able to
 * do something about anything that failed. Without it "offline-first" means
 * "your sales are somewhere, probably".
 *
 * The three groups answer three different questions. *Waiting* is "is it
 * working?" — normal, needs nothing. *Failed* is "did it give up?" — retryable,
 * usually after the network is genuinely back. *Needs attention* is "the server
 * refused this", and only a person can decide between trying again and letting
 * it go.
 */
const GROUPS: {
  key: string;
  title: string;
  blurb: string;
  states: SyncState[];
  tone: "neutral" | "warn" | "danger";
}[] = [
  {
    key: "waiting",
    title: "Waiting to upload",
    blurb: "Saved on this till. They will send themselves when the connection allows.",
    states: ["PENDING", "SYNCING"],
    tone: "neutral",
  },
  {
    key: "failed",
    title: "Gave up trying",
    blurb: "Tried many times without success. Nothing is lost — try again when the signal is back.",
    states: ["FAILED"],
    tone: "warn",
  },
  {
    key: "conflict",
    title: "Needs attention",
    blurb: "The server refused these. Repeating them will not help until somebody decides.",
    states: ["CONFLICT"],
    tone: "danger",
  },
];

export function SyncCentrePage() {
  const client = useQueryClient();
  const status = useSyncStore((s) => s.status);
  const lastSyncedAt = useSyncStore((s) => s.lastSyncedAt);
  const refreshCounts = useSyncStore((s) => s.refreshCounts);
  const [syncing, setSyncing] = useState(false);

  /**
   * Whether the browser has promised not to throw this terminal's data away.
   *
   * On the diagnostics screen rather than the till, because it is a
   * setup question — but it belongs *here*, beside the queue, since the queue is
   * exactly what gets lost when the answer is no.
   */
  const storage = useQuery({
    queryKey: ["sync", "storage"],
    queryFn: storageStatus,
    staleTime: 60_000,
  });

  const operations = useQuery({
    queryKey: ["sync", "outbox"],
    queryFn: () => outboxRepository.list(["PENDING", "SYNCING", "FAILED", "CONFLICT"]),
    refetchInterval: 5_000,
  });

  async function refresh() {
    await client.invalidateQueries({ queryKey: ["sync"] });
    await refreshCounts();
  }

  async function runNow() {
    setSyncing(true);
    try {
      await syncNow();
    } catch {
      // The button is "try now", not "guarantee success". Whatever went wrong is
      // already recorded against the operations themselves, which is where
      // somebody would look for it.
    } finally {
      setSyncing(false);
      await refresh();
    }
  }

  const all = operations.data ?? [];
  // "Waiting" means waiting to *upload*. A conflict is not waiting for the
  // network, it is waiting for a person — counting it here said "1 waiting"
  // above an empty queue and a row whose whole point is that retrying will not
  // help.
  const waiting = all.filter(
    (operation) => operation.state === "PENDING" || operation.state === "SYNCING",
  );
  const needsSomebody = all.length - waiting.length;

  return (
    <div className="space-y-5">
      {/* Only when the answer is no. A terminal that is protected does not need
          telling; one that is not needs telling before it loses a day. */}
      {storage.data && storage.data.supported && !storage.data.persistent && (
        <Card className="flex items-start gap-3 border-warn-500/30 bg-warn-50 p-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warn-700" aria-hidden />
          <div className="text-sm">
            <p className="font-medium text-ink-900">
              This browser has not promised to keep this till&rsquo;s data
            </p>
            <p className="mt-0.5 text-ink-600">
              Sales rung up with no signal live on this device until they upload, and a browser
              short of space may clear them. Installing SmartSpaza as an app — rather than using
              it in a browser tab — is what usually fixes this. See docs/TERMINAL-SETUP.md.
            </p>
          </div>
        </Card>
      )}

      <Card className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-ink-900">
            {status === "OFFLINE"
              ? "This till is offline"
              : waiting.length > 0
                ? `${plural(waiting.length, "operation")} waiting to upload`
                : needsSomebody > 0
                  ? `${plural(needsSomebody, "operation")} ${needsSomebody === 1 ? "needs" : "need"} a decision`
                  : "Everything is uploaded"}
          </h2>
          <p className="mt-0.5 text-sm text-ink-500">
            {lastSyncedAt
              ? `Last successful sync ${formatRelativeTime(lastSyncedAt)}.`
              : "This till has not synced yet."}
          </p>
        </div>

        <Button
          variant="primary"
          icon={<RefreshCw className={cn("size-4", syncing && "animate-spin")} aria-hidden />}
          disabled={syncing}
          onClick={() => void runNow()}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </Card>

      {all.length === 0 ? (
        <EmptyState
          icon={CloudOff}
          title="Nothing is waiting"
          description="Every sale made on this till has reached the server."
        />
      ) : (
        GROUPS.map((group) => {
          const rows = all.filter((operation) => group.states.includes(operation.state));
          if (rows.length === 0) return null;

          return (
            <Card key={group.key} flush className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-ink-900">
                    {group.title} <span className="tabular-nums text-ink-500">({rows.length})</span>
                  </h3>
                  <p className="mt-0.5 text-sm text-ink-500">{group.blurb}</p>
                </div>
              </div>

              <ul className="mt-3 divide-y divide-line">
                {rows.map((operation) => (
                  <OperationRow
                    key={operation.opId}
                    operation={operation}
                    tone={group.tone}
                    onChanged={() => void refresh()}
                  />
                ))}
              </ul>
            </Card>
          );
        })
      )}
    </div>
  );
}

function OperationRow({
  operation,
  tone,
  onChanged,
}: {
  operation: OutboxOperation;
  tone: "neutral" | "warn" | "danger";
  onChanged: () => void;
}) {
  const payload = operation.payload as { items?: unknown[]; payments?: { amount?: string }[] };
  const amount = payload.payments?.[0]?.amount;

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink-900">
          {LABELS[operation.type] ?? operation.type}
          {amount && <span className="ml-2 tabular-nums text-ink-600">R{amount}</span>}
        </span>
        <span className="block text-xs text-ink-500">
          {operation.businessDate} ·{" "}
          {formatRelativeTime(new Date(operation.createdAt).toISOString())}
          {operation.attempts > 0 && ` · ${operation.attempts} attempts`}
        </span>
        {operation.lastError && (
          <span className="mt-0.5 block text-xs text-danger-700">{operation.lastError}</span>
        )}
      </span>

      <Pill tone={tone === "danger" ? "danger" : tone === "warn" ? "warn" : "info"}>
        {operation.state.toLowerCase()}
      </Pill>

      {operation.state !== "PENDING" && operation.state !== "SYNCING" && (
        <div className="flex gap-1">
          <Button
            variant="secondary"
            size="sm"
            icon={<Upload className="size-3.5" aria-hidden />}
            onClick={async () => {
              await outboxRepository.requeue(operation.opId);
              onChanged();
            }}
          >
            Try again
          </Button>

          {operation.state === "CONFLICT" && (
            <Button
              variant="danger"
              size="sm"
              icon={<Trash2 className="size-3.5" aria-hidden />}
              onClick={async () => {
                await outboxRepository.abandon(operation.opId);
                onChanged();
              }}
            >
              Discard
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

const LABELS: Record<string, string> = {
  "sale.create": "Sale",
  "stock_movement.create": "Stock movement",
  "shift.open": "Shift opened",
  "shift.close": "Cash-up",
  // Was missing, so a queued pay-out showed as the raw string
  // "cash_movement.create" on the one screen a shopkeeper reads when something
  // has gone wrong.
  "cash_movement.create": "Cash in or out",
  "refund.create": "Refund",
};

/** Shown at the top of the list when something needs a decision. */
export function ConflictBanner({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <p className="flex items-start gap-2 rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      {count === 1
        ? "One item needs a decision before it can upload."
        : `${count} items need a decision before they can upload.`}
    </p>
  );
}
